import React, { useState, useEffect, useContext, useMemo } from "react";
import "./Bike.css";
import "./Mining.css";
import toast, { Toaster } from "react-hot-toast";
import { CartContext } from "../../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import "./SpecialProduct.css"
import AIS140Availability from "../../contexts/AIS140Availabilities"

const SpecialProduct = ({ websiteData }) => {
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("/images/placeholder-product.png")
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedRelayer, setSelectedRelayer] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isAIS, setIsAIS] = useState(false);


  const { cartItems, addToCart, updateCartItem, cartLoaded } = useContext(CartContext);
  const navigate = useNavigate();

  const existingCartItem = cartItems.find(
    (item) => Number(item?.device?.id) === Number(product?.selectedDevice?.id)
  );

  const isProductInCart = !!existingCartItem;

  useEffect(() => {
    if (!cartLoaded || !product?.selectedDevice) return;

    const cartItem = cartItems.find(
      (item) =>
        Number(item?.device?.id) === Number(product.selectedDevice.id)
    );

    if (cartItem) {
      setQuantity(cartItem.quantity || 1);
      setSelectedNetwork(cartItem.network || null);
      // setSelectedRelayer(cartItem.relayer || null);
      setSelectedRelayer(cartItem.isRelay ? device.relayAmt : null);
      setSelectedSubscription(cartItem.subscription || null);
      setSelectedState(cartItem.state || null);
      setSelectedCity(cartItem.city || null);
    }
  }, [cartLoaded, cartItems, product]);

  useEffect(() => {
    if (!websiteData?.[3]?.device?.length) return;

    const foundProduct = websiteData[3];

    // safely pick device
    const selectedDevice =
      foundProduct.device[1] || foundProduct.device[0];

    if (!selectedDevice) return;

    setProduct({
      ...foundProduct,
      selectedDevice,
    });

    setSelectedImage(
      selectedDevice?.image?.[1] ||
      selectedDevice?.image?.[0] ||
      "/images/placeholder-product.png"
    );

    const { network2gAmt, network4gAmt } = selectedDevice;

    if (network2gAmt === 0) {
      setSelectedNetwork("2G");
    } else if (network4gAmt === 0) {
      setSelectedNetwork("4G");
    }

    setIsAIS(
      selectedDevice?.name?.includes("AIS")
    );
  }, [websiteData]);

  const device = product?.selectedDevice;


  useEffect(() => {
    if (!device?.isRelay) return;

    if (device.relayAmt === 0) {
      setSelectedRelayer("included");
    } else {
      setSelectedRelayer("without");
    }
  }, [device]);

  useEffect(() => {
    if (!device?.isNetwork) return;

    const has4G = Number(device.network4gAmt) > 0;
    const has2G = Number(device.network2gAmt) > 0;

    if (has4G) {
      setSelectedNetwork("4G");
    } else if (has2G) {
      setSelectedNetwork("2G");
    } else {
      setSelectedNetwork("2G");
    }
  }, [device]);

  useEffect(() => {
    if (!device?.isSubscription) return;

    if (device.subscriptionYearlyAmt === 0) {
      setSelectedSubscription("yearly");
    } else {
      setSelectedSubscription("monthly");
    }
  }, [device]);

  const TAX_RATE = 0.18; // 18%

  const pricingCalculation = useMemo(() => {
    if (!device) return { discountedPrice: 0, totalPrice: 0, savings: 0, currentPrice: 0, minMRP: 0, maxMRP: 0, discountPercent: 0 };

    const base = Number(device.amount) || 0;
    const discount = Number(device.discount) || 0;

    const discountedBase = base - (base * discount) / 100;

    let optionsTotal = 0;
    if (selectedNetwork === "2G") optionsTotal += device.network2gAmt || 0;
    if (selectedNetwork === "4G") optionsTotal += device.network4gAmt || 0;
    if (selectedRelayer) optionsTotal += device.relayAmt || 0;
    if (selectedSubscription === "monthly") optionsTotal += device.subscriptionMonthlyAmt || 0;
    if (selectedSubscription === "yearly") optionsTotal += device.subscriptionYearlyAmt || 0;

    const discountedPrice = (discountedBase + optionsTotal) * (1 + TAX_RATE);

    const currentPrice = (base + optionsTotal) * (1 + TAX_RATE);

    const minMRP = (discountedBase) * (1 + TAX_RATE);

    const maxMRP = (discountedBase +
      (device.network2gAmt || 0) +
      (device.network4gAmt || 0) +
      (device.relayAmt || 0) +
      (device.subscriptionMonthlyAmt || 0) +
      (device.subscriptionYearlyAmt || 0)) * (1 + TAX_RATE);

    const totalPrice = discountedPrice * quantity;

    return {
      discountedPrice, currentPrice, minMRP, maxMRP, discountPercent: discount,
      savings: currentPrice - discountedPrice,
      totalPrice,
    };
  }, [
    device,
    selectedNetwork,
    selectedRelayer,
    selectedSubscription,
    quantity
  ]);


  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const selectedCityInfo = isAIS
    ? AIS140Availability[selectedState]?.find(city => city.name === selectedCity)
    : null;

  const pricing = useMemo(() => {
    const device = product?.selectedDevice;

    if (!device) {
      return {
        originalBase: 0,
        discountedBase: 0,
        optionsTotal: 0,
        unitPrice: 0,
        unitFinalPrice: 0,
        totalPrice: 0,
        savings: 0,
      };
    }

    const originalBase = Number(device.mrp || device.amount) || 0;
    const discountPercent = Number(device.discount) || 0;

    // ✅ discount only on base
    const discountedBase =
      originalBase - (originalBase * discountPercent) / 100;

    let optionsTotal = 0;

    if (selectedRelayer) {
      optionsTotal += Number(device.relayAmt) || 0;
    }

    if (selectedNetwork === "2G") {
      optionsTotal += Number(device.network2gAmt) || 0;
    } else if (selectedNetwork === "4G") {
      optionsTotal += Number(device.network4gAmt) || 0;
    }

    if (selectedSubscription === "monthly") {
      optionsTotal += Number(device.subscriptionMonthlyAmt) || 0;
    } else if (selectedSubscription === "yearly") {
      optionsTotal += Number(device.subscriptionYearlyAmt) || 0;
    }

    if (isAIS && selectedCityInfo?.price) {
      optionsTotal += Number(selectedCityInfo.price) || 0;
    }

    const unitPrice = discountedBase;
    const unitFinalPrice = discountedBase + optionsTotal;
    const totalPrice = unitFinalPrice * quantity;

    return {
      originalBase,
      discountedBase,
      optionsTotal,
      unitPrice,
      unitFinalPrice,
      totalPrice,
      savings: originalBase - discountedBase,
    };
  }, [
    product,
    selectedRelayer,
    selectedNetwork,
    selectedSubscription,
    selectedCityInfo,
    quantity,
    isAIS,
  ]);

  const getFinalPrice = (networkOverride = selectedNetwork) => {
    const device = product?.selectedDevice;
    if (!device) return 0;

    let price = Math.round(
      (device.amount || 0) * (1 - (device.discount || 0) / 100)
    );

    if (selectedRelayer) price += parseInt(selectedRelayer) || 0;

    if (networkOverride === "2G") price += parseInt(device.network2gAmt) || 0;
    if (networkOverride === "4G") price += parseInt(device.network4gAmt) || 0;

    if (selectedSubscription === "monthly") price += parseInt(device.subscriptionMonthlyAmt) || 0;
    if (selectedSubscription === "yearly") price += parseInt(device.subscriptionYearlyAmt) || 0;

    if (selectedCityInfo?.price) price += parseInt(selectedCityInfo.price) || 0;

    return price * quantity; // multiply by quantity
  };

  const calculatePricing = () => {
    const device = product?.selectedDevice;
    if (!device) return {
      originalBase: 0,
      displayAmount: 0,
      discountedBase: 0,
      unitFinalPrice: 0,
      totalPrice: 0
    };

    const originalBase = Number(device.amount) || 0;
    const discountPercent = Number(device.discount) || 0;
    const discountedBase = originalBase - (originalBase * discountPercent) / 100;

    let optionsTotal = 0;

    if (selectedRelayer) optionsTotal += Number(device.relayAmt) || 0;

    if (selectedNetwork === "2G") optionsTotal += Number(device.network2gAmt) || 0;
    else if (selectedNetwork === "4G") optionsTotal += Number(device.network4gAmt) || 0;

    if (selectedSubscription === "monthly") optionsTotal += Number(device.subscriptionMonthlyAmt) || 0;
    else if (selectedSubscription === "yearly") optionsTotal += Number(device.subscriptionYearlyAmt) || 0;

    if (isAIS && selectedCityInfo?.price) optionsTotal += Number(selectedCityInfo.price) || 0;

    const displayAmount = originalBase + optionsTotal; // For showing "original price + options"
    const unitFinalPrice = discountedBase + optionsTotal; // Price per unit after discount
    const totalPrice = unitFinalPrice * quantity; // Final total

    return { originalBase, displayAmount, discountedBase, unitFinalPrice, totalPrice };
  };

  // const pricing = calculatePricing();


  // const handleAddToCart = () => {
  //   const device = product?.selectedDevice;
  //   if (!device) return;

  //   let resolvedNetwork = selectedNetwork;
  //   if (!resolvedNetwork) {
  //     if (device.network2gAmt === 0) resolvedNetwork = "2G";
  //     else if (device.network4gAmt === 0) resolvedNetwork = "4G";
  //   }

  //   if (isAIS) {
  //     if (!selectedState) {
  //       toast.error("Please select a state");
  //       return;
  //     }
  //     if (!selectedCity) {
  //       toast.error("Please select a city");
  //       return;
  //     }
  //   }

  //   const allNetworkZeroOrExcluded =
  //     (device.network2gAmt === undefined || device.network2gAmt === -1) &&
  //     (device.network4gAmt === undefined || device.network4gAmt === -1);

  //   const allSubscriptionZero =
  //     (!device.subscriptionMonthlyAmt || device.subscriptionMonthlyAmt === 0) &&
  //     (!device.subscriptionYearlyAmt || device.subscriptionYearlyAmt === 0);

  //   if (device.isNetwork && !allNetworkZeroOrExcluded && !resolvedNetwork) {
  //     toast.error("Please select a network option before adding to cart");
  //     return;
  //   }

  //   if (device.isSubscription && !allSubscriptionZero && !selectedSubscription) {
  //     toast.error("Please select a subscription option before adding to cart");
  //     return;
  //   }

  //   const price = getFinalPrice(resolvedNetwork);

  //   const cartItem = {
  //     deviceId: device.id,
  //     product,
  //     quantity,
  //     totalAmount: getFinalPrice(),
  //     price: getFinalPrice() / quantity,
  //     name: device.name,
  //     model: device.model,
  //     discount: device.discount || 0,
  //     network: selectedNetwork,
  //     relayer: selectedRelayer,
  //     subscription: selectedSubscription,
  //     state: selectedState,
  //     city: selectedCity,
  //   };


  //   addToCart(cartItem);
  //   toast.success("Product added to cart!");
  // };

  const handleAddToCart = async () => {
    const device = product?.selectedDevice;
    if (!device) return;

    if (isAIS && (!selectedState || !selectedCity)) {
      toast.error("Please select your state and city");
      return;
    }

    const allNetworkZeroOrExcluded =
      (device.network2gAmt === undefined || device.network2gAmt === -1) &&
      (device.network4gAmt === undefined || device.network4gAmt === -1);

    const allSubscriptionZero =
      (!device.subscriptionMonthlyAmt || device.subscriptionMonthlyAmt === 0) &&
      (!device.subscriptionYearlyAmt || device.subscriptionYearlyAmt === 0);

    if (device.isNetwork && !allNetworkZeroOrExcluded && !selectedNetwork) {
      toast.error("Please select a network option before adding to cart");
      return;
    }

    if (device.isSubscription && !allSubscriptionZero && !selectedSubscription) {
      toast.error("Please select a subscription option before adding to cart");
      return;
    }

    // const price = getFinalPrice() / quantity;
    // const totalAmount = getFinalPrice();

    const cartItem = {
      deviceId: device.id,
      product: { selectedDevice: device },
      quantity,
      // totalAmount,
      // price,
      totalAmount: pricing.totalPrice,
      price: pricing.unitFinalPrice,
      name: device.name,
      model: device.model,
      discount: device.discount || 0,
      network: selectedNetwork,
      relayer: selectedRelayer,
      subscription: selectedSubscription,
      state: selectedState,
      city: selectedCity,
    };

    try {
      if (isProductInCart) {
        await updateCartItem(existingCartItem, cartItem);
        toast.success("Product updated in cart!");
      } else {
        await addToCart(cartItem);
        toast.success("Product added to cart!");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate("/cart");
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity(""); // reset city when state changes
  };

  const handleCityChange = (e) => setSelectedCity(e.target.value);

  if (!product) return null;

  return (
    <div className="special-product-mining-product">
      {/* <Toaster position="top-center" reverseOrder={false} /> */}

      {/* Image Section */}
      <div className="special-product-mining-product-gallery">
        <div className="special-product-mining-product-thumbnails">
          {device?.image?.slice(1).map((img, i) => (
            <img
              key={i}
              src={img}
              alt={`Thumbnail ${i + 1}`}
              className={`thumbnail ${selectedImage === img ? "active" : ""}`}
              onClick={() => setSelectedImage(img)}
              onError={(e) => (e.target.src = "/images/placeholder-product.png")}
            />
          ))}
        </div>

        {/* <div className="mining-product-main"> */}
        <img
          src={selectedImage}
          alt="Main product"
          className="special-product-main-image"
          onError={(e) => (e.target.src = "/images/placeholder-product.png")}
        />
        {/* </div> */}
      </div>

      {/* Product Details */}
      <div className="special-product-mining-product-details">
        <h2 className="mining-product-title-h2">
          {device?.name} {device?.model}
        </h2>

        {/* <div className="special-product-mining-product-features">
          <ul>
            {device?.points?.slice(0, 4)?.map((p, i) => (
              <li key={i}>{p.title}</li>
            ))}
          </ul>
        </div> */}

        <div className="special-product-features">
          <ul className="special-product-features-list">
            {device?.points?.slice(0, 4)?.map((p, i) => (
              <li key={i} className="special-product-feature-item">
                {/* <span className="special-product-feature-icon">✔</span> */}
                <span className="special-product-feature-text">{p.title}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* 
        <div style={{ display: "flex" }}>
          <p className="special-product-para-base">Base Price: ₹ <strong className="special-product-price-original">{pricing.displayAmount.toFixed(2)}</strong></p>
          <p className="special-product-para">Discounted Price: ₹ <strong className="special-product-price-discounted">{pricing.unitFinalPrice.toFixed(2)}</strong></p>
        </div> */}


        <div className="special-options-css">
          <div className="extra-product-details">
            {/* {device?.isRelay && device?.relayAmt > 0 && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Relayer Option</h3>
                <div className="option-btns">
                  <button
                    className={`option-btn ${selectedRelayer === device?.relayAmt ? "active" : ""
                      }`}
                    onClick={() =>
                      setSelectedRelayer((prev) =>
                        prev === device?.relayAmt ? null : device?.relayAmt
                      )
                    }
                  >
                    Relayer – ₹{device?.relayAmt}
                  </button>
                </div>
              </div>
            )} */}

            {/* {device?.isNetwork && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Network Options</h3>
                <div className="option-btns">
                  {device?.network2gAmt !== -1 && (
                    <button
                      // className={`option-btn ${
                      //   selectedNetwork === "2G" ? "active" : ""
                      // }`}
                      className={`option-btn active`}
                      onClick={() =>
                        device.network2gAmt > 0 &&
                        setSelectedNetwork((prev) => (prev === "2G" ? null : "2G"))
                      }
                      disabled={device.network2gAmt === 0}
                    >
                      2G – ₹
                      {device.network2gAmt === 0
                        ? "Included"
                        : device.network2gAmt}
                    </button>
                  )}

                  {device?.network4gAmt !== -1 && (
                    <button
                      className={`option-btn ${selectedNetwork === "4G" ? "active" : ""
                        }`}
                      onClick={() =>
                        device.network4gAmt > 0 &&
                        setSelectedNetwork((prev) => (prev === "4G" ? null : "4G"))
                      }
                      disabled={device.network4gAmt === 0}
                    >
                      4G – ₹
                      {device.network4gAmt === 0
                        ? "Included"
                        : device.network4gAmt}
                    </button>
                  )}
                </div>
              </div>
            )} */}

            {/* Subscription Options */}
            {/* {device?.isSubscription &&
              (device?.subscriptionMonthlyAmt > 0 ||
                device?.subscriptionYearlyAmt > 0) && (
                <div className="extra-detail-card">
                  <h3 className="option-headings">Subscription</h3>
                  <div className="option-btns">
                    {device?.subscriptionMonthlyAmt > 0 && (
                      <button
                        className={`option-btn ${selectedSubscription === "monthly" ? "active" : ""
                          }`}
                        onClick={() => setSelectedSubscription("monthly")}
                      >
                        Monthly – ₹{device?.subscriptionMonthlyAmt}
                      </button>
                    )}
                    {device?.subscriptionYearlyAmt > 0 && (
                      <button
                        className={`option-btn ${selectedSubscription === "yearly" ? "active" : ""
                          }`}
                        onClick={() => setSelectedSubscription("yearly")}
                      >
                        Yearly – ₹{device?.subscriptionYearlyAmt}
                      </button>
                    )}
                  </div>
                </div>
              )} */}

            {device?.isRelay && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Relayer Option</h3>

                <div className="option-btns">
                  {device.relayAmt === 0 ? (
                    <button className="option-btn active">
                      Relay Included
                    </button>
                  ) : (
                    <>
                      <button
                        className={`option-btn ${selectedRelayer === "without" ? "active" : ""}`}
                        onClick={() => setSelectedRelayer("without")}
                      >
                        Without Relay
                      </button>

                      <button
                        className={`option-btn ${selectedRelayer === "with" ? "active" : ""}`}
                        onClick={() => setSelectedRelayer("with")}
                      >
                        Relay Included – ₹{device.relayAmt}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {device?.isNetwork && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Network</h3>

                <div className="option-btns">
                  <button className="option-btn active">
                    Airtel
                  </button>
                </div>

                {(device.network2gAmt === 0 && device.network4gAmt === 0) ? (
                  <p className="option-note">
                    2G & 4G Included
                  </p>
                ) : (
                  <p className="option-note">
                    Network charges applied based on plan
                  </p>
                )}
              </div>
            )}

            {device?.isSubscription && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Subscription</h3>

                <div className="option-btns">
                  {device.subscriptionYearlyAmt === 0 ? (
                    <button className="option-btn active">
                      1 year subscription included
                    </button>
                  ) : (
                    <>
                      <button
                        className={`option-btn ${selectedSubscription === "monthly" ? "active" : ""}`}
                        onClick={() => setSelectedSubscription("monthly")}
                      >
                        1 month subscription
                      </button>

                      <button
                        className={`option-btn ${selectedSubscription === "yearly" ? "active" : ""}`}
                        onClick={() => setSelectedSubscription("yearly")}
                      >
                        1 year subscription
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* AIS CHANGES */}
            {isAIS && (
              <div className="extra-detail-card">
                <h3 className="option-headings">Location (AIS Only)</h3>
                <div className="special-product-option-btns">
                  <select
                    className="special-product-select"
                    value={selectedState || ""}
                    onChange={handleStateChange}
                  >
                    <option value="">Select State</option>
                    {Object.keys(AIS140Availability).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>

                  <select
                    className="special-product-select"
                    value={selectedCity || ""}
                    onChange={handleCityChange}
                    disabled={!selectedState}
                  >
                    <option value="">Select City</option>
                    {selectedState && AIS140Availability[selectedState].map(city => (
                      <option
                        key={city.name}
                        value={city.name}
                        disabled={!city.availability}
                      >
                        {city.name} {city.availability ? "" : "(Unavailable)"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* {selectedCityInfo && (
                  <div style={{ marginTop: "10px", fontSize: "12px" }}>
                    <strong>Service Area Price:</strong> ₹{selectedCityInfo.price} <br />
                    <strong>Available:</strong> {selectedCityInfo.availability ? "Yes" : "No"}
                  </div>
                )} */}
              </div>
            )}

          </div>

          {/* Final Price */}
          {/* <div className="special-product-final-price">
            <p>
              Total Price : ₹ <strong>{getFinalPrice()}</strong>
            </p>
          </div> */}

          <div>
            <div className="special-product-price-box">
              {pricingCalculation.minMRP < pricingCalculation.maxMRP && (
                <p className="product-overview-mrp-range">
                  MRP Range: ₹{pricingCalculation.minMRP.toFixed(0)} – ₹{pricingCalculation.maxMRP.toFixed(0)}
                </p>
              )}

              <div className="special-product-price-row">
                <span className="special-product-price-final">
                  ₹{pricingCalculation.discountedPrice.toFixed(0)}
                </span>
                <span className="special-product-price-per-item">/ item</span>

                {pricing.originalBase > 0 && (
                  <span className="special-product-price-old">
                    ₹{pricingCalculation.currentPrice.toFixed(0)}
                  </span>
                )}

                {device?.discount > 0 && (
                  <span className="special-product-price-discount">
                    {device.discount}% OFF
                  </span>
                )}
              </div>

              <p className="special-product-price-save">
                You save ₹{pricingCalculation.savings.toFixed(0)}
                {device?.discount > 0 && ` (${device.discount}%)`}
              </p>

              {pricingCalculation.optionsTotal > 0 && (
                <p className="special-product-price-addons">
                  Add-ons: ₹{pricingCalculation.optionsTotal}
                </p>
              )}

              <p className="special-product-price-quantity">
                {quantity} × ₹{pricingCalculation.discountedPrice.toFixed(0)}
              </p>

              <div className="special-product-price-total">
                <span>Item Total: </span>
                <span className="special-product-price-total-value">
                  ₹{pricingCalculation.totalPrice.toFixed(0)}
                </span>
              </div>

              <p className="special-product-price-tax">
                Inclusive of all taxes
              </p>

            </div>

            <div className="special-product-final-price">
              <p>
                {/* Base Price: ₹<strong>{pricing.displayAmount.toFixed(2)}</strong> <br /> */}
                {/* Discounted Unit Price: ₹<strong>{pricing.unitFinalPrice.toFixed(2)}</strong><br /> */}
                <strong>Total Price: ₹{pricing.totalPrice.toFixed(0)}</strong>
              </p>
            </div>

            {/* Quantity & Buttons */}
            <div className="special-mining-product-quantity">
              <div className="mining-product-quantity-controls">
                <div className="mining-product-quantity-selector">
                  <div className="mining-product-quantity-value">
                    <p>{quantity}</p>
                  </div>
                  <div className="mining-product-quantity-btns">
                    <div className="mining-product-quantity-btns-container">
                      <button className="quantity-up" onClick={incrementQuantity}>
                        <img
                          className="arrow-up"
                          src="/images/up-arrows.png"
                          alt="Increase quantity"
                        />
                      </button>
                      <button className="quantity-down" onClick={decrementQuantity}>
                        <img
                          className="arrow-down"
                          src="/images/arrow-down-sign-to-navigate.png"
                          alt="Decrease quantity"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                {/* <button className="add-to-cart-btn" onClick={handleAddToCart}>
                Add to Cart
              </button> */}
                <button className="special-product-add-to-cart-btn" onClick={handleAddToCart}>
                  {isProductInCart ? "Update Product" : "Add to Cart"}
                </button>
                <button className="special-product-buy-it-now-btn" onClick={handleBuyNow}>
                  Buy it now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialProduct;
